import { yupResolver } from "@hookform/resolvers/yup";
import { mdiArrowRight } from "@mdi/js";
import Icon from "@mdi/react";
import { useEffect, useMemo, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useToggle } from "usehooks-ts";
import { array, object, string } from "yup";

import AutoComplete from "components/AutoComplete";
import Button from "components/Button";
import Checkbox from "components/Checkbox";
import ConfirmationModal from "components/ConfirmationModal";
import ContentSwitch from "components/ContentSwitch";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import PhoneNumber from "components/PhoneNumber";
import Select from "components/Select";
import { PHONE_CODES } from "constants/phone-code-list";
import {
  BUSINESS_TYPES,
  EQUIPMENT_TYPES,
  NUMBER_UNSUPPORTED_TYPES,
  VEHICLE_NUMBER_TYPES
} from "constants/signup";
import { phoneRegex } from "helpers/regex";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { IOption } from "models/Form";
import { AddressInfo, SignupBusinessFormData, SignupUser, SignUpUserFormData } from "models/User";
import { getUserByFmcsaAPI } from "services/api/user";
import { AppDispatch } from "store";
import { signupUser } from "store/auth/actions";
import { handleLoading } from "store/global/loader";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

const businessFormSchema = object<Partial<SignupBusinessFormData>>({
  businessName: string().required("This field is required"),
  businessType: object<IOption>().required("This field is required"),
  businessAddress: object().required("This field is required"),
  phoneNumber: string()
    .required("This field is required")
    .max(19, "Phone number limit exceeded")
    .matches(phoneRegex, "Phone number is not valid"),
  equipmentType: array()
    .of(
      object().shape({
        id: string().required("ID is required"),
        label: string().required("Label is required"),
        value: string().required("Value is required")
      })
    )
    .when("businessType", {
      is: (businessType: IOption) => (businessType ? businessType.value === "carrier" : false),
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.nullable()
    }),
  transIdNumber: string().when("businessType", {
    is: (businessType: IOption) =>
      businessType ? !NUMBER_UNSUPPORTED_TYPES.includes(businessType.value) : false,
    then: (businessFormSchema) => businessFormSchema.required("This field is required"),
    otherwise: (businessFormSchema) => businessFormSchema.nullable()
  })
});

interface IProps {
  basicFormData?: SignUpUserFormData;
  setStepCallback: (step: number) => void;
}

export default function BusinessSection({ basicFormData, setStepCallback }: Readonly<IProps>) {
  const { isDesktop } = useDeviceType();
  const dialList = useMemo(
    () =>
      PHONE_CODES.map((item, index) => ({
        id: index,
        label: item.dial_code,
        value: item.dial_code
      })),
    [PHONE_CODES]
  );
  const [businessType, setBusinessType] = useState<IOption>();
  const [transIdType, setTransIdType] = useState(VEHICLE_NUMBER_TYPES[0]);
  const [transIdNumber, setTransIdNumber] = useState<{ mc?: number; dot?: number }>({
    mc: undefined,
    dot: undefined
  });

  const dispatch = useDispatch<AppDispatch>();
  const [isSmsAlertModalOpen, toggleSmsAlertModalOpen] = useToggle();
  const { trackAllEvents } = useAnalytics();

  useEffect(() => {
    setValue(
      "transIdNumber",
      transIdType?.value
        ? transIdNumber[transIdType.value as keyof typeof transIdNumber]
        : undefined
    );
  }, [transIdType]);

  const {
    control,
    register,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<Partial<SignupBusinessFormData>>({
    resolver: yupResolver(businessFormSchema)
  });

  const onSubmit = async (businessFormData: SignupBusinessFormData) => {
    const {
      username,
      email,
      password,
      language,
      businessName,
      phoneCode,
      phoneNumber,
      transIdType,
      transIdNumber,
      enableSmsAlert,
      equipmentType,
      businessAddress: { street_one, address, city, country, state, zip }
    } = { ...basicFormData, ...businessFormData };
    const signupUserData = {
      email,
      password,
      language: language?.value,
      type: businessType?.value,
      referrer_url: document.referrer || "",
      [businessType?.value ?? "carrier"]: {
        equipment_types: equipmentType?.map((item) => item.value) ?? [],
        dba: businessName,
        contact_phone: `${(phoneCode?.value ?? "+1") + (phoneNumber ?? "")}`,
        contact_name: username,
        language: language?.value,
        ...(transIdType?.value ? { [transIdType.value]: transIdNumber } : {}),
        enable_sms_alerts: enableSmsAlert,
        address: street_one ?? address,
        city,
        state,
        zip,
        country
      }
    };
    try {
      await dispatch(signupUser(signupUserData as SignupUser));
      setStepCallback(2);
      trackAllEvents("auth_signup_next_page2");
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  const getUserByFMCSA = async () => {
    const { transIdNumber, transIdType } = getValues();

    if (transIdNumber && transIdType) {
      dispatch(handleLoading(true));
      try {
        const data = await getUserByFmcsaAPI({ [transIdType.value]: transIdNumber } as any);
        const { street_one, city, country, state, zip, dba, phone } = data.data;
        const businessAddress: AddressInfo = {
          street_one,
          city,
          country,
          state,
          zip
        };
        setValue("businessName", dba);
        setValue("businessAddress", businessAddress);
        setValue("phoneNumber", phone);
      } catch (error) {
        /* empty */
      } finally {
        dispatch(handleLoading(false));
      }
    }
  };

  return (
    <form className={styles.businessForm} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="businessType"
        control={control}
        render={({ field: { value, onChange } }) => {
          return (
            <Select
              value={value}
              options={BUSINESS_TYPES}
              error={errors.businessType as FieldError}
              placeholder="What type of Business Are You?"
              onChange={(value) => {
                setBusinessType(value);
                onChange(value);
              }}
              label="Business Type"
              data-cy="signup-business-select"
            />
          );
        }}
      />

      {!NUMBER_UNSUPPORTED_TYPES.includes(businessType?.value ?? "shipper") && (
        <div className="flex flex-col gap-2">
          <ContentSwitch
            name="transIdType"
            control={control}
            changeCallback={setTransIdType}
            options={VEHICLE_NUMBER_TYPES}
            data-cy="signup-transport-id-type-switcher"
          />
          <Input
            name="transIdNumber"
            type="number"
            inputMode="numeric"
            defaultValue={transIdNumber[transIdType.value as keyof typeof transIdNumber]}
            placeholder={transIdType.label}
            register={register}
            onBlur={async () => {
              setTransIdNumber({
                ...transIdNumber,
                [transIdType.value]: getValues().transIdNumber
              });
              getUserByFMCSA();
            }}
            error={errors.transIdNumber}
            data-cy="signup-transport-id-input"
          />
        </div>
      )}
      <Input
        name="businessName"
        label="Business Name"
        placeholder="Your Business Name"
        register={register}
        error={errors.businessName}
        data-cy="signup-business-name-input"
      />
      <Controller
        name="businessAddress"
        control={control}
        render={({ field: { value = null, onChange }, fieldState: { error } }) => (
          <GoogleAutoComplete
            label="Business Address"
            placeholder="Your Address"
            error={error}
            value={value}
            onChange={(addressInfo: AddressInfo) => onChange(addressInfo)}
            data-cy="signup-address-input"
          />
        )}
      />
      {(businessType?.value ?? "carrier") === "carrier" && (
        <AutoComplete
          name="equipmentType"
          label="Equipment Type"
          placeholder="Equipment Type"
          options={EQUIPMENT_TYPES}
          control={control}
          error={errors.equipmentType as FieldError}
          isMulti
          data-cy="signup-equipment-type-select"
        />
      )}
      <div className={styles.phoneNumberWrapper}>
        <span className={styles.phoneNumberLabel}>Phone Number</span>
        <div className={styles.phoneNumberInputWrapper}>
          <Controller
            name="phoneCode"
            control={control}
            render={({ field: { value = dialList[230], onChange } }) => (
              <Select
                value={value}
                onChange={onChange}
                options={dialList}
                className="flex-none"
                data-cy="signup-phone-code-select"
                direction="up"
              />
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { value = "", onChange } }) => (
              <PhoneNumber
                value={value}
                onChange={onChange}
                placeholder="Phone Number"
                disableCountryCode
                error={errors.phoneNumber}
                className="flex-1"
                wrapperClass="flex-1"
                data-cy="signup-phonenumber-input"
              />
            )}
          />
        </div>

        <Checkbox
          name="enableSmsAlert"
          register={register}
          error={errors.enableSmsAlert}
          wrapperClass="mobile:mt-16 mt-2 text-left"
          data-cy="signup-enableSmsAlert-checkbox">
          I would like to receive{" "}
          <a
            href="#"
            tabIndex={0}
            onClick={(event) => {
              event.preventDefault();
              toggleSmsAlertModalOpen();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggleSmsAlertModalOpen();
              }
            }}
            className="underline text-primary">
            Important text messages regarding my operations
          </a>
        </Checkbox>
      </div>
      <Button
        type="submit"
        size="lg"
        suffixNode={
          !isDesktop && (
            <span className={styles.arrowRightIcon}>
              <Icon path={mdiArrowRight} className="m-auto" />
            </span>
          )
        }
        className="mt-6 desktop:!px-16 desktop:w-fit"
        data-cy="signup-submit-button-second"
        loading={isSubmitting}>
        NEXT
      </Button>

      <ConfirmationModal
        title="Comfreight Text Message"
        isConfirmModalOpen={isSmsAlertModalOpen}
        toggleConfirmModal={toggleSmsAlertModalOpen}
        isHideHeaderIcon
        isHideCancelButton
        content={
          <div className="flex flex-col gap-3">
            <p>
              If enabled, we’ll send you a text(SMS) when there are urgent matters that need your
              attention, such as:
            </p>

            <ul className="pl-6 list-disc list-outside">
              <li>There are document issues with a funding request</li>
              <li>You are underbid on a bid</li>
              <li>And others</li>
            </ul>

            <p>
              Depending on your activity on our platform, you can expect between 1 text every month
              to 1 text per day.
            </p>
            <p>Charges may apply.</p>
          </div>
        }
      />
    </form>
  );
}
